package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.AudioData
import fr.manyverse.app.domain.repository.AudioStorageRepository
import fr.manyverse.app.infrastructure.filestorage.LocalFileStorage

/**
 * Local implementation of AudioStorageRepository.
 * Stores audio files on the device using LocalFileStorage.
 */
class LocalAudioStorageRepository(
    private val fileStorage: LocalFileStorage
) : AudioStorageRepository {

    companion object {
        private const val SOUNDS_DIRECTORY = "sounds"
    }

    override suspend fun uploadAudio(
        userId: String,
        soundId: String,
        audioData: AudioData
    ): Result<String> {
        val extension = getExtensionFromMimeType(audioData.mimeType)
        val path = "$SOUNDS_DIRECTORY/$userId/$soundId.$extension"
        return fileStorage.saveFile(path, audioData.bytes).map { path }
    }

    override suspend fun deleteAudio(userId: String, soundId: String): Result<Unit> {
        // Delete all possible extensions since we don't know which one was used
        val extensions = listOf("mp3", "wav", "ogg", "flac", "aac", "m4a")
        extensions.forEach { ext ->
            val path = "$SOUNDS_DIRECTORY/$userId/$soundId.$ext"
            fileStorage.deleteFile(path)
        }
        return Result.success(Unit)
    }

    override suspend fun downloadAudio(audioPath: String): Result<ByteArray> {
        return fileStorage.readFile(audioPath)
    }

    private fun getExtensionFromMimeType(mimeType: String): String = when (mimeType) {
        "audio/mpeg", "audio/mp3" -> "mp3"
        "audio/wav", "audio/wave", "audio/x-wav" -> "wav"
        "audio/ogg" -> "ogg"
        "audio/flac", "audio/x-flac" -> "flac"
        "audio/aac" -> "aac"
        "audio/mp4", "audio/x-m4a", "audio/m4a" -> "m4a"
        else -> "mp3"
    }
}
